/**
 * PhilosophyCallout.tsx — icon-led darker-cream aside for philosophy principles.
 *
 * Semantically and visually distinct from <magicTip>:
 *   magicTip     = practical tip, compact, ✦ unicode, no icon, --cream-2 background.
 *   philosophyCallout = principle / deeper "why", icon left rail, --cream-3 background.
 *
 * CSS: .blog-philosophy-callout in globals.css
 *   Layout: flex row, 48px icon left rail, text column.
 *   Background: --cream-3 (#E2D9CE) — one step darker than --cream-2.
 *   Border: 1px solid --border-subtle-cream.
 *   Radius: --radius-md (8px).
 *   Heading: Cormorant Garamond italic, --gold-on-cream.
 *   Body: --font-sans, --text-dark-muted.
 *   Semantic: <aside role="note"> — same pattern as <magicTip>.
 *
 * Icon rule: inline SVG only, never <img src="*.svg"> — kit §12 §4 v1.4.
 * Icon set: 10 approved icons from PhilosophyIcon.tsx. Any outside this set
 *           requires Aria sign-off before use.
 *
 * Usage in MDX:
 *   <philosophyCallout icon="slow-practice-depth" heading="Slow Practice">
 *     Speed comes from depth, not from rushing.
 *   </philosophyCallout>
 *
 * Props:
 *   icon     — one of the 10 approved PhilosophyIconName values. Required.
 *   heading  — 1–3 word label. Required.
 *   children — rich-text body (Tina AST, rendered via TinaMarkdown). Required.
 *
 * Added 2026-05-09 per Aria component spec (video-blog migrations cycle 5).
 */

import PhilosophyIcon, { type PhilosophyIconName } from './PhilosophyIcon';

// TinaMarkdown is imported dynamically at the BlogPostPageClient layer;
// the component receives children as a Tina rich-text AST and renders
// it via the parent's component map. Here children is ReactNode after
// the parent resolver runs.
interface PhilosophyCalloutProps {
  icon: PhilosophyIconName;
  heading: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
}

export default function PhilosophyCallout({ icon, heading, children }: PhilosophyCalloutProps) {
  return (
    <aside className="blog-philosophy-callout" role="note">
      <div className="blog-philosophy-callout__icon" aria-hidden="true">
        <PhilosophyIcon name={icon} width={48} height={48} />
      </div>
      <div className="blog-philosophy-callout__text">
        <p className="blog-philosophy-callout__heading">{heading}</p>
        <div className="blog-philosophy-callout__body">
          {children}
        </div>
      </div>
    </aside>
  );
}
